import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Btn, Badge, Modal, Input, Select, Loading, EmptyState } from '../components/ui';
import { useRef } from 'react';

export default function MachineMaster() {
  const [machines, setMachines] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterFactory, setFilterFactory] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ factory_id: '', machine_type: '', description: '', count_in_unit: '' });
  const [error, setError] = useState('');
  const [uploadMsg, setUploadMsg] = useState('');
  const fileRef = useRef();

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Machine master'; }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterFactory ? { factory_id: filterFactory } : {};
      const [m, f] = await Promise.all([api.get('/machine-types', { params }), api.get('/factories')]);
      setMachines(m.data); setFactories(f.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterFactory]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => {
    setEditing(null);
    setForm({ factory_id: filterFactory || factories[0]?.id || '', machine_type: '', description: '', count_in_unit: '' });
    setError(''); setModalOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({ factory_id: m.factory_id, machine_type: m.machine_type, description: m.description || '', count_in_unit: m.count_in_unit });
    setError(''); setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.factory_id || !form.machine_type || !form.count_in_unit) { setError('All required fields must be filled'); return; }
    if (isNaN(form.count_in_unit) || parseInt(form.count_in_unit) <= 0) { setError('Count must be a positive number'); return; }
    try {
      if (editing) await api.put(`/machine-types/${editing.id}`, { ...form, count_in_unit: parseInt(form.count_in_unit) });
      else await api.post('/machine-types', { ...form, count_in_unit: parseInt(form.count_in_unit) });
      setModalOpen(false); fetchAll();
    } catch (e) { setError(e.response?.data?.error || 'Error saving'); }
  };

  const totalCount = machines.reduce((s, m) => s + m.count_in_unit, 0);

  const downloadTemplate = () => {
    const rows = [['Machine Type', 'Description', 'Count', 'Unit']];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = 'machine_upload_template.csv'; a.click();
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const formData = new FormData(); formData.append('file', file);
    setUploadMsg('Uploading…');
    try {
      const { data } = await api.post('/machine-types/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadMsg(`✓ Inserted ${data.inserted}, updated ${data.updated}${data.errors?.length ? ` · ${data.errors.length} skipped` : ''}`);
      fetchAll();
    } catch (e) { setUploadMsg('Upload failed: ' + (e.response?.data?.error || e.message)); }
    e.target.value = '';
  };

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Machine master <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{machines.length} records</span></div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Machine types and counts per unit</div>
        </div>
        <Select value={filterFactory} onChange={setFilterFactory} placeholder="All units"
          options={factories.map(f => ({ value: f.id, label: f.name }))} />
        <Btn size="sm" onClick={downloadTemplate}>Download template</Btn>
        <Btn size="sm" onClick={() => fileRef.current.click()}>Upload Excel</Btn>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleUpload} />
        <Btn variant="primary" size="sm" onClick={openAdd}>+ Add new</Btn>
      </div>

      {uploadMsg && (
        <div style={{ marginBottom: 10, fontSize: 12, padding: '6px 12px', borderRadius: 7,
          background: uploadMsg.startsWith('✓') ? 'var(--grn-bg)' : 'var(--red-bg)',
          color: uploadMsg.startsWith('✓') ? 'var(--grn)' : 'var(--red)' }}>
          {uploadMsg}
        </div>
      )}
      {machines.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {machines.map(m => (
            <span key={m.id} style={{ background: 'var(--acc-bg)', color: 'var(--acc-t)', border: '0.5px solid var(--acc)', padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 500 }}>
              {m.machine_type} × {m.count_in_unit}
            </span>
          ))}
          <span style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', padding: '3px 10px', borderRadius: 20, fontSize: 11.5 }}>
            Total: {totalCount}
          </span>
        </div>
      )}

      <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
        {loading ? <Loading /> : machines.length === 0
          ? <EmptyState message="No machines yet" action={<Btn variant="primary" onClick={openAdd}>+ Add machine type</Btn>} />
          : (
            <table className="lc-table">
              <thead>
                <tr>
                  <th>Machine type</th>
                  <th>Description</th>
                  <th>Count in unit</th>
                  <th>Factory</th>
                  <th>Division</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {machines.map(m => (
                  <tr key={m.id}>
                    <td style={{ fontWeight: 500 }}>{m.machine_type}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 11.5 }}>{m.description || '—'}</td>
                    <td><span style={{ fontWeight: 500, fontSize: 15, color: 'var(--acc)' }}>{m.count_in_unit}</span></td>
                    <td style={{ fontSize: 11.5 }}>{m.factory_name}</td>
                    <td style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{m.division_name || '—'}</td>
                    <td><Badge type={m.is_active ? 'green' : 'red'}>{m.is_active ? 'Active' : 'Inactive'}</Badge></td>
                    <td><a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => openEdit(m)}>Edit</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}
        title={editing ? 'Edit machine type' : 'Add machine type'} width={380}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Factory</div>
            <Select value={form.factory_id} onChange={v => setForm(p => ({ ...p, factory_id: v }))}
              placeholder="Select unit…" options={factories.map(f => ({ value: f.id, label: f.name }))}
              style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Machine type <span style={{ color: 'var(--text-tertiary)' }}>(e.g. SNLS, DNLS, OL)</span></div>
            <Input value={form.machine_type} onChange={v => setForm(p => ({ ...p, machine_type: v.toUpperCase() }))}
              placeholder="e.g. SNLS" style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Description</div>
            <Input value={form.description} onChange={v => setForm(p => ({ ...p, description: v }))}
              placeholder="e.g. Single Needle Lockstitch" style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Count in unit</div>
            <Input value={form.count_in_unit} onChange={v => setForm(p => ({ ...p, count_in_unit: v }))}
              type="number" placeholder="e.g. 24" style={{ width: '100%' }} />
          </div>
          {error && <div style={{ fontSize: 11.5, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 9px', borderRadius: 6 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setModalOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSave}>Save</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}